import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandL, LabelL } from '#src/atoms/typography/index.tsx';
import { TextureBackground } from '#src/atoms/ui/TextureBackground/index.tsx';
import { TextField } from '#src/components/form/Form/index.tsx';
import { NicknameForm } from '#src/components/form/NicknameForm/index.tsx';
import { EnterCodeButton } from '#src/components/landing/EnterCodeButton/index.tsx';
import { EnterCodeInline } from '#src/components/landing/EnterCodeInline/index.tsx';
import { SignupLeaguesCarousel } from '#src/components/landing/SignupLeaguesCarousel/index.tsx';
import { SignupPromoBanner } from '#src/components/landing/SignupPromoBanner/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { glossary } from '#src/lib/glossary.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

const Wrapper = styled(animated.div)`
  display: flex;
  align-items: flex-end;
  position: relative;
  isolation: isolate;
  justify-content: center;
  overflow: hidden;
  transition: 300ms;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  padding-inline: var(--double-unit);

  & > * {
    z-index: 1;
  }

  &:not(.active) {
    cursor: pointer;
  }

  &.step1 {
    align-items: stretch;
  }

  &.step2 {
    align-items: start;
  }

  &.step2::after {
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
    backdrop-filter: blur(15px);
  }

  @media ${laptopAndAbove} {
    padding-inline: 0;
  }

  &.active::before {
    content: '';
    inset: 0;
    opacity: 0.35;
    position: absolute;
    pointer-events: none;
    background: linear-gradient(0deg, #101010 0%, rgba(16, 16, 16, 0) 143.7%);
  }
`;

const StartButton = styled(Button)`
  transition: 250ms;
  margin-bottom: 3vh;
  backdrop-filter: blur(33px);
  background-color: rgba(255, 255, 255, 0.11);

  @media ${laptopAndAbove} {
    margin-bottom: calc(var(--unit) * 18);
  }

  &:hover {
    background-color: white;
    color: var(--c-black);
  }
`;
const Container = styled(Vertical)`
  width: min(calc(var(--100vw) - 32px), 520px);
`;
const StyledInput = styled(TextField)`
  height: 55px;
  backdrop-filter: blur(10px);
  border: 1px solid var(--c-white);
  background-color: rgba(255, 255, 255, 0.11);

  input {
    font-size: 20px;
    text-align: center;
    font-family: 'Helvetica';
    color: var(--c-white);
    background-color: transparent;
  }

  input::placeholder {
    opacity: 0.75;
  }
`;

const FinalStepWrapper = styled(Vertical)`
  /* accounts for absolute-positioned header */
  margin-top: 56px;
  gap: 0;
  overflow: hidden;
  @media ${laptopAndAbove} {
    gap: calc(var(--unit) * 8);
  }
`;

const ErrorWrapper = styled.div`
  border-radius: 8px;
  background-color: var(--c-red-400);
  padding: var(--intermediate-unit) var(--double-unit);

  span {
    color: white;
  }
`;

const PromoBannerWrapper = styled.div`
  align-self: stretch;
  @media ${laptopAndAbove} {
    align-self: center;
    min-width: 700px;
  }
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
`;

const LastStepTitle = styled(Vertical)`
  z-index: 1;
  text-align: center;
  text-overflow: ellipsis;
  max-width: 100%;
  overflow: hidden;
`;

const UsernameFormWrapper = styled(animated.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  align-items: center;
  justify-content: center;
  padding: 0 var(--double-unit);
  /* accounts for absolute-positioned header */
  margin-top: 70px;
`;

const UsernameForInnerWrapper = styled(Vertical)`
  flex: 1;
  justify-content: flex-start;
`;

const SecondaryStepTitle = styled(BrandL)`
  text-wrap: balance;
`;

const ContentWrapper = styled(Vertical)`
  flex: 1;
`;

const messages = defineMessages({
  lastStepTitle: {
    defaultMessage: 'Welcome to Sorare {username}',
  },
  placeholder: {
    defaultMessage: 'Enter your Manager name',
  },
});

type UsernameFormProps = {
  sport: Sport;
  onSuccess: ({ nickname }: { nickname?: string }) => void;
  error: string | undefined;
};

const UsernameForm = ({
  sport,
  onSuccess,
  error: parentError,
}: UsernameFormProps) => {
  const [nicknameError, setNicknameError] = useState(false);
  const { formatMessage } = useIntlContext();
  const {
    flags: { enableInviteFriends = false },
  } = useFeatureFlags();

  return (
    <NicknameForm
      sport={sport}
      onSuccess={({ nickname }) => {
        setNicknameError(false);
        onSuccess({ nickname });
      }}
      onError={() => setNicknameError(true)}
      render={(Error, SubmitButton, opts, values) => (
        <Container center gap={3}>
          <StyledInput
            type="text"
            name="nickname"
            placeholder={formatMessage(messages.placeholder)}
          />
          {!!nicknameError && (
            <ErrorWrapper>
              <Error />
            </ErrorWrapper>
          )}
          {!!parentError && <ErrorWrapper>{parentError}</ErrorWrapper>}
          {enableInviteFriends ? <EnterCodeInline /> : <EnterCodeButton />}
          <SubmitButton
            size="medium"
            type="submit"
            color="secondary"
            disabled={!values.nickname}
          >
            {formatMessage(glossary.continue)}
          </SubmitButton>
        </Container>
      )}
    />
  );
};

type Props = {
  step: 0 | 1 | 2;
  active?: boolean;
  sport: Sport;
  onClick: () => void;
  className?: string;
  background: string;
  WalletPortal: ReactNode;
  startButtonText: string;
  goToNextStep: () => void;
  secondaryStepTitle: string;
  secondaryStepSubtitle: string;
  nickname?: string;
  usernameError: string | undefined;
};

export const SportContainer = ({
  step,
  active,
  sport,
  onClick,
  className,
  background,
  goToNextStep,
  WalletPortal,
  startButtonText,
  secondaryStepTitle,
  secondaryStepSubtitle,
  nickname: defaultNickname,
  usernameError,
}: Props) => {
  const [username, setUsername] = useState<string | undefined>(defaultNickname);
  const {
    flags: { useLandingPagePromoBanner = false },
  } = useFeatureFlags();
  const secondTransition = useTransition(step === 1 && active, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    config: { duration: 300 },
  });

  const displayTextureBackground = !!active && sport === Sport.FOOTBALL;
  return (
    <Wrapper
      onClick={() => !active && onClick()}
      className={classNames(className, {
        active,
        step1: step > 0,
        step2: step === 2,
      })}
      style={
        displayTextureBackground
          ? undefined
          : { backgroundImage: `url(${background})` }
      }
    >
      {!!displayTextureBackground && (
        <StyledTextureBackground background="wave1" color="var(--c-blue-400)" />
      )}
      {step === 0 && (
        <StartButton size="large" color="quaternary">
          {startButtonText}
        </StartButton>
      )}
      {secondTransition(
        (styles, item) =>
          item && (
            <ContentWrapper>
              <UsernameFormWrapper style={styles}>
                {!!useLandingPagePromoBanner && (
                  <PromoBannerWrapper>
                    <SignupPromoBanner sport={sport} />
                  </PromoBannerWrapper>
                )}
                <UsernameForInnerWrapper gap={6} center>
                  <Vertical gap={2} center className="text-center">
                    <StarBall width={28} height={28} />
                    <SecondaryStepTitle as="h1">
                      {secondaryStepTitle}
                    </SecondaryStepTitle>
                    <LabelL as="h3">{secondaryStepSubtitle}</LabelL>
                  </Vertical>
                  <UsernameForm
                    sport={sport}
                    onSuccess={({ nickname }) => {
                      setUsername(nickname);
                      goToNextStep();
                    }}
                    error={usernameError}
                  />
                </UsernameForInnerWrapper>
              </UsernameFormWrapper>
              {sport === Sport.FOOTBALL && <SignupLeaguesCarousel />}
            </ContentWrapper>
          )
      )}
      {step === 2 && !!active && (
        <FinalStepWrapper center>
          <LastStepTitle gap={2} center>
            <StarBall width={28} height={28} />
            <BrandL>
              <FormattedMessage
                {...messages.lastStepTitle}
                values={{
                  username,
                }}
              />
            </BrandL>
          </LastStepTitle>
          {WalletPortal}
        </FinalStepWrapper>
      )}
    </Wrapper>
  );
};
